import { useGetUserDocumentVersionReadStatus } from 'src/api/customer-admin'
import { CenteredSpinner } from 'src/components/Spinner'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'

interface UserVersionApprovalTableProps {
  customerId: EntityId
  documentId: EntityId
  userId: string
}

export const UserVersionApprovalTable: React.FC<UserVersionApprovalTableProps> = ({
  customerId,
  documentId,
  userId,
}) => {
  const { t } = useTranslation(['global-document-status', 'document', 'common', 'global-documents'])
  const { data, isLoading } = useGetUserDocumentVersionReadStatus(customerId, documentId, userId)

  if (isLoading)
    return (
      <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CenteredSpinner />
      </Box>
    )

  if (!data || data.length === 0)
    return <EmptyStatusCard content={t('noVersionApprovalData', { ns: 'global-document-status' })} imageWidth="100px" />

  return (
    <VersionListContainer>
      <VersionListHeader>
        <VersionListCell flex={1}>{t('version', { ns: 'document' })}</VersionListCell>
        <VersionListCell flex={2}>{t('description', { ns: 'common' })}</VersionListCell>
        <VersionListCell flex={1}>{t('status', { ns: 'common' })}</VersionListCell>
        <VersionListCell flex={1}>{t('approvedAt', { ns: 'global-documents' })}</VersionListCell>
      </VersionListHeader>
      {data.map(version => (
        <VersionListRow key={version.documentVersionId}>
          <VersionListCell flex={1}>{version.documentVersion}</VersionListCell>
          <VersionListCell flex={2}>{version.documentVersionDescription || '-'}</VersionListCell>
          <VersionListCell flex={1}>
            {version.isApproved
              ? t('approved', { ns: 'global-document-status' })
              : version.documentVersionId === data[0]?.documentVersionId
              ? t('pending', { ns: 'global-document-status' })
              : t('notApproved', { ns: 'global-document-status' })}
          </VersionListCell>
          <VersionListCell flex={1}>
            {version.approvedAt ? new Date(version.approvedAt + 'Z').toISOString().split('T')[0] : '-'}
          </VersionListCell>
        </VersionListRow>
      ))}
    </VersionListContainer>
  )
}
getThemeColor
const VersionListContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: '5px',
  overflow: 'hidden',
  border: `1px solid ${getThemeColor(theme, MUIThemeColors.backgroundMain)}`,
  background: getThemeColor(theme, MUIThemeColors.white),
}))

const VersionListHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  background: getThemeColor(theme, MUIThemeColors.secondaryLight),
  fontWeight: 600,
  padding: '12px 0',
  borderBottom: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
}))

const VersionListRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 0',
  borderBottom: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}))

const VersionListCell = styled(Typography)<{ flex?: number }>(({ flex = 1 }) => ({
  flex,
  padding: '0 12px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))
